import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const QuickContactModal = ({ donor, request, onClose, onLogContact }) => {
  const [method, setMethod] = useState('whatsapp');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Generate public request link
  const requestLink = request?.id 
    ? `${window.location.origin}/request/${request.id}`
    : `${window.location.origin}/donate`;

  const templates = {
    whatsapp: {
      urgent: `🩸 *URGENT BLOOD REQUEST* 🩸\n\nHi ${donor?.name},\n\nWe have an URGENT need for *${request?.blood_group || donor?.bloodGroup}* blood at ${request?.hospital?.name || request?.hospital_name || 'a nearby hospital'} in ${request?.hospital?.location || request?.hospital_location || donor?.district}.\n\n⏰ *When:* ${request?.required_by || 'ASAP'}\n🏥 *Location:* ${request?.hospital?.name || request?.hospital_name || 'Hospital'}, ${request?.hospital?.location || request?.hospital_location || donor?.district}\n\n👉 *Click to view & respond:*\n${requestLink}\n\nYour donation can SAVE A LIFE! 💪\n\nPlease click the link above to mark your interest or reply here:\n✅ *YES* - I'm available\n❌ *NO* - Not available\n\nThank you! 🙏\nOCYM Blood Donation Team`,
      
      standard: `🩸 *Blood Donation Request* 🩸\n\nHi ${donor?.name},\n\nWe have a blood donation request for *${request?.blood_group || donor?.bloodGroup}* in ${donor?.district}.\n\n👉 *Click to view & respond:*\n${requestLink}\n\nCan you help? Please click the link above or reply here if you're available.\n\nThank you! 🙏\nOCYM Blood Donation Team`,
      
      followup: `👋 Hi ${donor?.name},\n\nJust following up on our blood donation request.\n\nStill looking for *${request?.blood_group || donor?.bloodGroup}* donors in ${donor?.district}.\n\n👉 *Click to view & respond:*\n${requestLink}\n\nAre you available to donate? 🩸\n\nThanks!\nOCYM Team`
    },
    sms: {
      urgent: `URGENT: Blood need ${request?.blood_group || donor?.bloodGroup} at ${request?.hospital?.name || request?.hospital_name || 'hospital'}. Click to respond: ${requestLink} . Reply YES/NO. OCYM`,
      
      standard: `Hi ${donor?.name}, blood donation request for ${request?.blood_group || donor?.bloodGroup}. Click to respond: ${requestLink} . Reply YES/NO. OCYM`,
      
      followup: `Follow-up: Still need ${request?.blood_group || donor?.bloodGroup} donors. Click to respond: ${requestLink} . OCYM`
    },
    email: {
      urgent: `Subject: 🩸 URGENT: Blood Donation Needed - ${request?.blood_group || donor?.bloodGroup}\n\nDear ${donor?.name},\n\nWe have an URGENT blood donation request:\n\nBlood Group: ${request?.blood_group || donor?.bloodGroup}\nHospital: ${request?.hospital?.name || request?.hospital_name || 'Hospital Name'}\nLocation: ${request?.hospital?.location || request?.hospital_location || donor?.district}\nRequired By: ${request?.required_by || 'ASAP'}\n\n👉 Click here to view details and mark your interest:\n${requestLink}\n\nYour donation can save a life!\n\nPlease click the link above to respond, or reply to this email if you can help.\n\nThank you,\nOCYM Blood Donation Team`,
      
      standard: `Subject: Blood Donation Request - ${request?.blood_group || donor?.bloodGroup}\n\nDear ${donor?.name},\n\nWe hope this message finds you well.\n\nWe have a blood donation request for ${request?.blood_group || donor?.bloodGroup} blood group in ${donor?.district}.\n\n👉 Click here to view details and mark your interest:\n${requestLink}\n\nIf you're available and eligible to donate, please click the link above or reply to this email.\n\nThank you for your service to the community!\n\nBest regards,\nOCYM Blood Donation Team`,
      
      followup: `Subject: Follow-up: Blood Donation Request\n\nDear ${donor?.name},\n\nWe're following up on our previous request for ${request?.blood_group || donor?.bloodGroup} blood donors in ${donor?.district}.\n\n👉 Click here to view details and mark your interest:\n${requestLink}\n\nIf you're now available to donate, please click the link above or reply to this email.\n\nThank you,\nOCYM Team`
    }
  };

  const [templateType, setTemplateType] = useState('standard');

  useEffect(() => {
    if (donor) {
      setMessage(templates[method]?.[templateType] || '');
    }
  }, [donor, method, templateType]);

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const handleSend = async () => {
    setIsSending(true);
    try {
      let url = '';
      const phone = donor?.phoneNumber?.replace(/\D/g, '');
      const encodedMsg = encodeURIComponent(message);

      switch (method) {
        case 'whatsapp':
          url = `https://wa.me/91${phone}?text=${encodedMsg}`;
          if (isMobile) {
            // Mobile: Use location.href for better compatibility
            window.location.href = url;
            toast.success('Opening WhatsApp...');
          } else {
            // Desktop: Try to open, fallback to copy
            const opened = window.open(url, '_blank');
            if (!opened || opened.closed || typeof opened.closed === 'undefined') {
              // Popup blocked or failed, copy to clipboard
              await navigator.clipboard.writeText(message);
              toast.success(
                <div>
                  <p className="font-bold">Message copied!</p>
                  <p className="text-sm">Phone: {donor?.phoneNumber}</p>
                  <p className="text-xs mt-1">WhatsApp blocked - paste manually</p>
                </div>,
                { duration: 5000 }
              );
            } else {
              toast.success('WhatsApp Web opened!');
            }
          }
          break;
        case 'sms':
          url = `sms:${phone}?body=${encodedMsg}`;
          if (isMobile) {
            window.location.href = url;
            toast.success('Opening SMS app...');
          } else {
            await navigator.clipboard.writeText(message);
            toast.success(
              <div>
                <p className="font-bold">Message copied!</p>
                <p className="text-sm">Phone: {donor?.phoneNumber}</p>
              </div>,
              { duration: 5000 }
            );
          }
          break;
        case 'email':
          const subject = encodeURIComponent(`Blood Donation Request - ${donor?.bloodGroup}`);
          url = `mailto:${donor?.email}?subject=${subject}&body=${encodedMsg}`;
          window.location.href = url;
          toast.success('Email client opened!');
          break;
        case 'call':
          url = `tel:${phone}`;
          window.location.href = url;
          toast.success('Calling...');
          break;
      }

      // Log the contact
      await onLogContact?.({
        donorId: donor.id,
        method,
        message,
        status: 'sent'
      });

      // Close modal for call and email
      if (method === 'call' || method === 'email') {
        onClose();
      }
      // For WhatsApp/SMS, stay open so they can retry if needed
    } catch (err) {
      console.error('Contact error:', err);
      toast.error('Failed to open. Please try again or copy manually.');
    } finally {
      setIsSending(false);
    }
  };

  if (!donor) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white w-full sm:rounded-2xl sm:max-w-lg sm:max-h-[90vh] overflow-hidden shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-xl">
                🩸
              </div>
              <div>
                <h3 className="font-bold text-lg">Contact {donor.name}</h3>
                <p className="text-red-100 text-sm">{donor.bloodGroup} • {donor.district}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">×</button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Method Selection */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { id: 'whatsapp', icon: '📱', label: 'WhatsApp', color: 'bg-green-500' },
              { id: 'sms', icon: '💬', label: 'SMS', color: 'bg-blue-500' },
              { id: 'call', icon: '📞', label: 'Call', color: 'bg-purple-500' },
              { id: 'email', icon: '✉️', label: 'Email', color: 'bg-orange-500' }
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMethod(m.id)}
                className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all ${
                  method === m.id 
                    ? `${m.color} text-white shadow-lg scale-105` 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className="text-xl">{m.icon}</span>
                <span className="text-xs font-medium">{m.label}</span>
              </button>
            ))}
          </div>

          {/* Template Selection */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { id: 'standard', label: 'Standard', emoji: '📋' },
              { id: 'urgent', label: 'Urgent', emoji: '🚨' },
              { id: 'followup', label: 'Follow-up', emoji: '👋' }
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTemplateType(t.id)}
                className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  templateType === t.id
                    ? 'bg-red-100 text-red-700 ring-2 ring-red-500'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>{t.emoji}</span>
                {t.label}
              </button>
            ))}
          </div>

          {/* Message Preview */}
          {method !== 'call' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm min-h-[120px] resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Your message..."
              />
              <p className="text-xs text-gray-500 text-right">{message.length} characters</p>
            </div>
          )}

          {method === 'call' && (
            <div className="bg-blue-50 p-4 rounded-xl text-center">
              <p className="text-blue-800 font-medium">📞 Ready to call</p>
              <p className="text-blue-600 text-sm">{donor.phoneNumber}</p>
            </div>
          )}

          {/* Donor Info Card */}
          <div className="bg-gray-50 p-3 rounded-xl text-sm space-y-1">
            <p><span className="text-gray-500">Phone:</span> <span className="font-medium">{donor.phoneNumber || 'N/A'}</span></p>
            <p><span className="text-gray-500">Email:</span> <span className="font-medium">{donor.email || 'N/A'}</span></p>
            <p><span className="text-gray-500">Location:</span> <span className="font-medium">{donor.location}, {donor.district}</span></p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || (!message && method !== 'call')}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSending ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <>
                {method === 'call' ? '📞' : '✈️'} {method === 'call' ? 'Call Now' : 'Send'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickContactModal;
